import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Panel, BrandButton } from "@/components/PageHeader";
import { EntryDialog } from "@/components/master-list/EntryDialog";
import { api, errMsg } from "@/lib/api";
import { useCanExport } from "@/context/AppContext";
import { Pager, usePager } from "@/components/Pager";

const Confirm = ({ state, onClose }) => (
  <AlertDialog open={!!state} onOpenChange={(v) => !v && onClose()}>
    <AlertDialogContent data-testid="master-confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>{state?.title}</AlertDialogTitle>
        <AlertDialogDescription>{state?.text}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="master-confirm-no">No, keep</AlertDialogCancel>
        <AlertDialogAction onClick={state?.run} className="bg-[#E60000] hover:bg-[#B30000]" data-testid="master-confirm-yes">Yes, delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const exportCsv = async () => {
  try {
    const r = await api.get("/master-list/export", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `gamls-master-list-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};

const Row = ({ m, checked, onCheck, onEdit, onDelete }) => (
  <tr className={checked ? "bg-[#FFF0F0]" : ""} data-testid={`master-row-${m.id}`}>
    <td className="px-3 py-2 w-10"><Checkbox checked={checked} onCheckedChange={onCheck} aria-label="Select record" data-testid={`master-select-${m.id}`} /></td>
    <td className="px-3 py-2 font-mono text-xs">{m.gamls_id || "—"}</td>
    <td className="px-3 py-2 font-mono text-xs">{m.staff_id || "—"}</td>
    <td className="px-3 py-2">{m.name || "—"}</td>
    <td className="px-3 py-2">
      <div className="flex justify-end gap-1">
        <button type="button" onClick={onEdit} className="w-8 h-8 grid place-items-center rounded-md hover:bg-gray-100" aria-label="Edit record" data-testid={`master-edit-${m.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={onDelete} className="w-8 h-8 grid place-items-center rounded-md hover:bg-[#FFF0F0] hover:text-[#E60000]" aria-label="Delete record" data-testid={`master-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </td>
  </tr>
);

export const MasterEntries = ({ reloadKey, onChanged }) => {
  const canExport = useCanExport();
  const [q, setQ] = useState("");
  const [data, setData] = useState({ entries: [], total: 0, matches: 0 });
  const [sel, setSel] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [tick, setTick] = useState(0);
  const pager = usePager(q);
  useEffect(() => {
    const t = setTimeout(() => api.get("/master-list", { params: { q, page: pager.page, size: pager.size } }).then((r) => { setData(r.data); setSel((s) => s.filter((id) => r.data.entries.some((e) => e.id === id))); }), 250);
    return () => clearTimeout(t);
  }, [q, reloadKey, tick, pager.page, pager.size]);
  const changed = () => { setTick((t) => t + 1); onChanged(); };
  const run = (title, text, fn) => setConfirm({ title, text, run: async () => { try { const r = await fn(); toast.success(`${r.data.deleted} record${r.data.deleted === 1 ? "" : "s"} deleted`); setSel([]); changed(); } catch (e) { toast.error(errMsg(e)); } } });
  const ids = data.entries.map((e) => e.id);
  const allOn = ids.length > 0 && ids.every((id) => sel.includes(id));
  return (
    <Panel title={`Master list (${data.total} records)`} testId="master-entries-panel">
      <div className="flex flex-wrap gap-2 mb-3">
        {canExport && <BrandButton variant="outline" onClick={exportCsv} disabled={!data.total} data-testid="master-export-button"><Download className="w-4 h-4" />Export</BrandButton>}
        <BrandButton variant="outline" onClick={() => run("Delete the whole master list?", `All ${data.total} records will be removed and every member and registration will show as Not in Good Standing until a new list is added.`, () => api.delete("/master-list"))}
          disabled={!data.total} className="hover:!border-[#E60000] hover:!text-[#E60000]" data-testid="master-clear-button"><Trash2 className="w-4 h-4" />Delete all</BrandButton>
        <BrandButton onClick={() => setEdit({ entry: null })} data-testid="master-add-button"><Plus className="w-4 h-4" />Add record</BrandButton>
      </div>
      <div className="relative mb-3"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search GAMLS ID, Staff ID or name" className="pl-9 h-10" data-testid="master-search-input" /></div>
      {sel.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md bg-[#111] text-white px-3 py-2 text-sm" data-testid="master-bulk-bar">
          <span className="font-semibold" data-testid="master-selected-count">{sel.length} selected</span>
          <button type="button" onClick={() => setSel([])} className="text-xs underline text-gray-300 hover:text-white" data-testid="master-clear-selection">Clear selection</button>
          <button type="button" onClick={() => run(`Delete ${sel.length} selected record${sel.length === 1 ? "" : "s"}?`, "People with these IDs will no longer be in Good Standing.", () => api.post("/master-list/delete", { ids: sel }))}
            className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#E60000] hover:bg-[#B30000] font-semibold transition-colors" data-testid="master-bulk-delete"><Trash2 className="w-3.5 h-3.5" />Delete selected</button>
        </div>
      )}
      <div className="max-h-[480px] overflow-auto border rounded-md">
        <table className="w-full text-sm" data-testid="master-entries-table">
          <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 w-10"><Checkbox checked={allOn} onCheckedChange={(c) => setSel(c ? ids : [])} aria-label="Select all shown" data-testid="master-select-all" /></th>
              {["GAMLS ID", "Staff ID", "Name", ""].map((h) => <th key={h} className="text-left font-bold px-3 py-2">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.entries.map((m) => (
              <Row key={m.id} m={m} checked={sel.includes(m.id)} onCheck={(c) => setSel((s) => (c ? [...s, m.id] : s.filter((x) => x !== m.id)))}
                onEdit={() => setEdit({ entry: m })} onDelete={() => run("Delete this record?", `${m.gamls_id || m.staff_id}${m.name ? ` (${m.name})` : ""} will no longer be in Good Standing.`, () => api.post("/master-list/delete", { ids: [m.id] }))} />
            ))}
          </tbody>
        </table>
        {!data.entries.length && <div className="p-6 text-center text-sm text-gray-500" data-testid="master-empty">{data.total ? "No records match your search." : "The master list is empty. Add a record or upload a file."}</div>}
      </div>
      <Pager {...pager} total={data.matches} testId="master-pager" />
      <EntryDialog open={!!edit} entry={edit?.entry} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); changed(); }} />
      <Confirm state={confirm} onClose={() => setConfirm(null)} />
    </Panel>
  );
};
